import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { PageBackButton } from '@/components/common/PageBackButton'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { Button } from '@/components/ui/button'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useDeleteGrower, useGrowers } from '@/hooks/use-growers-query'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Ô tìm kiếm trước đây nằm trong `useState` nên không vào URL và cũng không có
 * nhãn — chỉ có placeholder (R7, R9). `textFilter` lo cả debounce.
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.grower_search_label,
        placeholder: m.grower_search_placeholder,
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/growers/',
)({
    component: GrowersPage,
    validateSearch: FILTERS.validateSearch,
})

/**
 * Dòng phụ của card mobile: tên thường gọi đứng trước số điện thoại vì đó là
 * thứ phân biệt hai hộ trùng tên — card chỉ có một dòng phụ nên phải ưu tiên.
 */
function growerSubtitle(row: { nickname?: string; phone?: string }): string {
    const parts = [row.nickname, row.phone].filter(Boolean)
    return parts.length > 0 ? parts.join(' · ') : '—'
}

/** Export riêng để test render trực tiếp (route bị code-split khi build). */
export function GrowersPage() {
    const navigate = useNavigate()
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('growers.write')
    const { values, barProps } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                to: '/agriculture/growers',
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data } = useGrowers({
        per_page: 100,
        search: values.q || undefined,
    })
    const rows = data?.data ?? []

    const deleteMut = useDeleteGrower()
    // Chạm cả card mobile mở chi tiết; tên hộ vẫn là <Link> để mở tab mới được.
    const openDetail = (id: string) =>
        void navigate({ to: '/agriculture/growers/$id', params: { id } })

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {values.q ? m.list_empty_filtered() : m.common_no_data()}
            </span>
            {values.q ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.grower_title()}
                    </h1>
                </div>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() =>
                            navigate({ to: '/agriculture/growers/new' })
                        }
                    >
                        <Plus className="size-4" /> {m.grower_new()}
                    </Button>
                )}
            </div>

            <ListFilterBar {...barProps} />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {rows.length === 0 ? emptyState : null}
                {rows.map((row) => (
                    <ListCard
                        key={row.id}
                        onClick={() => openDetail(row.id)}
                        title={
                            <Link
                                to="/agriculture/growers/$id"
                                params={{ id: row.id }}
                                className={LIST_CARD_TITLE_CLASS}
                            >
                                {row.name}
                            </Link>
                        }
                        subtitle={growerSubtitle(row)}
                        actions={
                            canWrite ? (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={m.common_edit()}
                                        onClick={() =>
                                            void navigate({
                                                to: '/agriculture/growers/$id/edit',
                                                params: { id: row.id },
                                            })
                                        }
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={m.common_delete()}
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    m.grower_delete_confirm(),
                                                )
                                            ) {
                                                deleteMut.mutate(row.id)
                                            }
                                        }}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                </>
                            ) : null
                        }
                        fields={[
                            {
                                label: m.grower_address(),
                                value: row.address ?? '—',
                                full: true,
                            },
                        ]}
                    />
                ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.grower_name()}</TableHead>
                            <TableHead>{m.grower_nickname()}</TableHead>
                            <TableHead>{m.grower_phone()}</TableHead>
                            <TableHead>{m.grower_address()}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5}>{emptyState}</TableCell>
                            </TableRow>
                        ) : null}
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <Link
                                        to="/agriculture/growers/$id"
                                        params={{ id: row.id }}
                                        className="font-medium text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary"
                                    >
                                        {row.name}
                                    </Link>
                                </TableCell>
                                <TableCell>{row.nickname || '—'}</TableCell>
                                <TableCell>{row.phone ?? '—'}</TableCell>
                                <TableCell>{row.address ?? '—'}</TableCell>
                                <TableCell className="text-right space-x-1">
                                    {canWrite && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() =>
                                                    navigate({
                                                        to: '/agriculture/growers/$id/edit',
                                                        params: { id: row.id },
                                                    })
                                                }
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                    if (
                                                        window.confirm(
                                                            m.grower_delete_confirm(),
                                                        )
                                                    ) {
                                                        deleteMut.mutate(row.id)
                                                    }
                                                }}
                                            >
                                                <Trash2 className="size-4" />
                                            </Button>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
